import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'signature_pad';
import { resolveApiUrl } from '../services/api';

// Public, unauthenticated consent-signing page.
// Routed via early-return in App.tsx when pathname starts with /sign-consent/.
// No auth header, no Sidebar — the token in the URL is the only credential.

interface ConsentData {
  templateName: string;
  clinicName: string;
  patientName: string;
  procedures: string[];
  filledHtml: string;
  requiresSignature: boolean;
}

type Status = 'loading' | 'ready' | 'signed' | 'declined' | 'invalid' | 'error';

function extractToken(pathname: string): string {
  const m = pathname.match(/\/sign-consent\/([^/?#]+)/);
  return m ? m[1] : '';
}

const PublicConsentSign: React.FC = () => {
  const { t } = useTranslation();
  const token = extractToken(window.location.pathname);
  const [status, setStatus] = useState<Status>('loading');
  const [data, setData] = useState<ConsentData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const padRef = useRef<SignaturePad | null>(null);

  // Load the filled term.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setStatus('invalid'); return; }
      try {
        const res = await fetch(resolveApiUrl(`/api/consent/sign-consent/${token}`));
        if (res.status === 404) { if (!cancelled) setStatus('invalid'); return; }
        if (!res.ok) { if (!cancelled) setStatus('error'); return; }
        const json = await res.json();
        if (!cancelled) {
          setData(json.data);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Init the signature pad once the canvas is mounted.
  const initPad = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || padRef.current) return;
    // Scale for crisp lines on high-DPI displays.
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);
    padRef.current = new SignaturePad(canvas, { penColor: '#111', backgroundColor: 'rgba(255,255,255,0)' });
  }, []);

  useEffect(() => {
    if (status === 'ready') {
      // Defer to next frame so the canvas has its layout size.
      const id = requestAnimationFrame(initPad);
      return () => cancelAnimationFrame(id);
    }
  }, [status, initPad]);

  const clearPad = () => padRef.current?.clear();

  const submit = async () => {
    setErrorMsg('');
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      setErrorMsg(t('publicConsent.signBeforeSubmit'));
      return;
    }
    setSubmitting(true);
    try {
      const signature = pad.toDataURL('image/png');
      const res = await fetch(resolveApiUrl(`/api/consent/sign-consent/${token}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature }),
      });
      if (res.status === 404) { setStatus('invalid'); return; }
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setStatus('signed');
      } else {
        setErrorMsg(json.message || t('publicConsent.signatureError'));
      }
    } catch {
      setErrorMsg(t('publicConsent.connectionError'));
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(resolveApiUrl(`/api/consent/sign-consent/${token}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decline: true }),
      });
      if (res.status === 404) { setStatus('invalid'); return; }
      if (res.ok) setStatus('declined');
    } catch {
      setErrorMsg(t('publicConsent.connectionError'));
    } finally {
      setSubmitting(false);
    }
  };

  const wrap: React.CSSProperties = {
    minHeight: '100vh', background: '#f3f4f6', padding: '24px 16px',
    fontFamily: 'system-ui, sans-serif', color: '#111',
  };
  const card: React.CSSProperties = {
    maxWidth: 720, margin: '0 auto', background: '#fff', borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: 32,
  };

  if (status === 'loading') {
    return <div style={wrap}><div style={card}>{t('common.loading')}</div></div>;
  }
  if (status === 'invalid') {
    return <div style={wrap}><div style={card}>
      <h2>{t('publicConsent.invalidTitle')}</h2>
      <p style={{ color: '#6b7280' }}>{t('publicConsent.invalidBody')}</p>
    </div></div>;
  }
  if (status === 'error') {
    return <div style={wrap}><div style={card}>
      <h2>{t('publicConsent.errorTitle')}</h2>
      <p style={{ color: '#6b7280' }}>{t('publicConsent.errorBody')}</p>
    </div></div>;
  }
  if (status === 'signed') {
    return <div style={wrap}><div style={card}>
      <h2>{t('publicConsent.signedTitle')}</h2>
      <p style={{ color: '#6b7280' }}>{t('publicConsent.signedBody', { name: data?.patientName })}</p>
    </div></div>;
  }
  if (status === 'declined') {
    return <div style={wrap}><div style={card}>
      <h2>{t('publicConsent.declinedTitle')}</h2>
      <p style={{ color: '#6b7280' }}>{t('publicConsent.declinedBody')}</p>
    </div></div>;
  }

  return (
    <div style={wrap}>
      <div style={card}>
        {data?.clinicName && <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{data.clinicName}</div>}
        <h2 style={{ margin: '0 0 16px' }}>{data?.templateName || t('publicConsent.defaultTitle')}</h2>

        <div
          style={{
            border: '1px solid #e5e7eb', borderRadius: 8, padding: 20,
            maxHeight: 360, overflowY: 'auto', marginBottom: 24, fontSize: 14, lineHeight: 1.6,
          }}
          // The term HTML is authored by the clinic admin (trusted) and the
          // server escapes all patient-supplied placeholder values before fill.
          dangerouslySetInnerHTML={{ __html: data?.filledHtml || '' }}
        />

        <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>{t('publicConsent.signBelow')}</div>
        <canvas
          ref={canvasRef}
          style={{
            width: '100%', height: 180, border: '2px dashed #d1d5db',
            borderRadius: 8, touchAction: 'none', background: '#fff',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <button onClick={clearPad} type="button" style={{
            background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 13,
          }}>{t('publicConsent.clear')}</button>
        </div>

        {errorMsg && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{errorMsg}</div>}

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={submit}
            disabled={submitting}
            type="button"
            style={{
              flex: 1, background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none',
              padding: '14px 24px', borderRadius: 8, fontWeight: 600, fontSize: 15,
              cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? t('publicConsent.submitting') : t('publicConsent.signAndAccept')}
          </button>
          <button
            onClick={decline}
            disabled={submitting}
            type="button"
            style={{
              background: 'none', border: '1px solid #d1d5db', color: '#6b7280',
              padding: '14px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            {t('publicConsent.decline')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicConsentSign;
